import { Component, OnInit } from '@angular/core';
import {
  ContaReceberListaItem,
  FinContaReceberListaService,
  OpcaoIdDescricao
} from 'src/app/service/fin-conta-receber-lista.service';
import { FinSantanderCnabService } from 'src/app/service/fin-santander-cnab.service';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';
import { throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-gerar-cnab',
  templateUrl: './gerar-cnab.component.html',
  styleUrls: ['./gerar-cnab.component.scss']
})
export class GerarCnabComponent implements OnInit {
  itens: ContaReceberListaItem[] = [];
  itensPaginados: ContaReceberListaItem[] = [];
  loading = false;
  paginaAtual = 1;
  itensPorPagina = 15;

  documentos: OpcaoIdDescricao[] = [];
  usuarios: OpcaoIdDescricao[] = [];
  tiposRecebimento: OpcaoIdDescricao[] = [];

  filtroIdFinDocumento = 0;
  filtroNomePagador = '';
  filtroDataEmissaoInicio = '';
  filtroDataEmissaoFim = '';
  filtroDataVencimentoInicio = '';
  filtroDataVencimentoFim = '';
  filtroDataRecebimentoInicio = '';
  filtroDataRecebimentoFim = '';
  filtroIdTipoRecebimento = 0;
  filtroObservacao = '';
  filtroCnabEnviado: 'T' | 'S' | 'N' = 'T';
  /** Para remessa: carregar apenas títulos com boleto já gerado. */
  filtroBoletoGerado: 'T' | 'S' | 'N' = 'S';

  selecionados = new Set<number>();

  constructor(
    private finLista: FinContaReceberListaService,
    private ajaxService: AjaxService,
    private util: UtilService,
    private santanderCnab: FinSantanderCnabService
  ) {}

  ngOnInit(): void {
    this.carregarOpcoes();
    this.carregar();
  }

  get totalPaginas(): number {
    return Math.ceil(this.itens.length / this.itensPorPagina) || 1;
  }

  get todosDaPaginaSelecionados(): boolean {
    return (
      this.itensPaginados.length > 0 &&
      this.itensPaginados.every((i) => this.selecionados.has(i.id))
    );
  }

  carregarOpcoes(): void {
    this.ajaxService.get(`${API}financeiro/FinContaReceber/ConsultarOpcoes`).subscribe({
      next: (response: any) => {
        if (response?.hasErrors) {
          return;
        }
        const data = this.desempacotarResponse(response);
        const documentosData = data.documentos || data.docs || [];
        const usuariosData = data.usuarios || data.clientes || data.usuariospagador || [];
        const tiposData = data.tiposrecebimento || data.tipopagamento || data.tiposPagamento || [];
        this.documentos = this.normalizarLista(documentosData)
          .map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') }))
          .filter((x) => x.id > 0);
        this.usuarios = this.normalizarLista(usuariosData)
          .map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') }))
          .filter((x) => x.id > 0);
        this.tiposRecebimento = this.normalizarLista(tiposData)
          .map((x: any) => ({ id: Number(x.id || 0), descricao: String(x.descricao || '') }))
          .filter((x) => x.id > 0);
      }
    });
  }

  montarQueryLista(): URLSearchParams {
    const params = new URLSearchParams();
    params.set('boletoGerado', 'true');
    if (this.filtroIdFinDocumento > 0) {
      params.set('idFinDocumento', String(this.filtroIdFinDocumento));
    }
    if (this.filtroNomePagador.trim()) {
      params.set('nomePagador', this.filtroNomePagador.trim());
    }
    const [emiIni, emiFim] = this.ordenarParDatas(this.filtroDataEmissaoInicio, this.filtroDataEmissaoFim);
    if (emiIni) params.set('dataEmissaoInicio', emiIni);
    if (emiFim) params.set('dataEmissaoFim', emiFim);
    const [venIni, venFim] = this.ordenarParDatas(this.filtroDataVencimentoInicio, this.filtroDataVencimentoFim);
    if (venIni) params.set('dataVencimentoInicio', venIni);
    if (venFim) params.set('dataVencimentoFim', venFim);
    const [recIni, recFim] = this.ordenarParDatas(this.filtroDataRecebimentoInicio, this.filtroDataRecebimentoFim);
    if (recIni) params.set('dataRecebimentoInicio', recIni);
    if (recFim) params.set('dataRecebimentoFim', recFim);
    if (this.filtroIdTipoRecebimento > 0) {
      params.set('idTipoRecebimento', String(this.filtroIdTipoRecebimento));
    }
    if (this.filtroObservacao.trim()) {
      params.set('observacao', this.filtroObservacao.trim());
    }
    if (this.filtroCnabEnviado === 'S') {
      params.set('cnabEnviado', 'true');
    } else if (this.filtroCnabEnviado === 'N') {
      params.set('cnabEnviado', 'false');
    }
    return params;
  }

  carregar(): void {
    this.loading = true;
    this.util.showLoading();
    this.finLista.carregarListaGerarCnab(this.montarQueryLista()).subscribe({
      next: (lista) => {
        this.itens = lista;
        this.paginaAtual = 1;
        this.selecionados.clear();
        this.atualizarPaginacao();
        this.loading = false;
        this.util.closeLoading();
      },
      error: () => {
        this.itens = [];
        this.itensPaginados = [];
        this.selecionados.clear();
        this.loading = false;
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Não foi possível carregar contas a receber.');
      }
    });
  }

  limparFiltros(): void {
    this.filtroIdFinDocumento = 0;
    this.filtroNomePagador = '';
    this.filtroDataEmissaoInicio = '';
    this.filtroDataEmissaoFim = '';
    this.filtroDataVencimentoInicio = '';
    this.filtroDataVencimentoFim = '';
    this.filtroDataRecebimentoInicio = '';
    this.filtroDataRecebimentoFim = '';
    this.filtroIdTipoRecebimento = 0;
    this.filtroObservacao = '';
    this.filtroCnabEnviado = 'T';
    this.filtroBoletoGerado = 'S';
    this.carregar();
  }

  toggleSelecionado(id: number): void {
    if (this.selecionados.has(id)) {
      this.selecionados.delete(id);
    } else {
      this.selecionados.add(id);
    }
  }

  toggleTodosPagina(): void {
    if (this.todosDaPaginaSelecionados) {
      this.itensPaginados.forEach((i) => this.selecionados.delete(i.id));
    } else {
      this.itensPaginados.forEach((i) => this.selecionados.add(i.id));
    }
  }

  isSelecionado(id: number): boolean {
    return this.selecionados.has(id);
  }

  gerarRemessa(): void {
    const ids = Array.from(this.selecionados);
    if (!ids.length) {
      return;
    }
    this.util.showLoading();
    this.santanderCnab
      .obterConfig()
      .pipe(
        switchMap((cfg) => {
          if (!cfg.configurado) {
            return throwError(
              () =>
                new Error(
                  'Configure o Santander na tela Configuração CNAB Santander (menu Contas a Receber) antes de gerar a remessa.'
                )
            );
          }
          return this.santanderCnab.gerarRemessaDownload(ids);
        })
      )
      .subscribe({
        next: () => {
          this.util.closeLoading();
          this.util.toasterShowSuccess(
            'Remessa',
            'Arquivo .txt baixado. Envie ao banco e importe o retorno na tela Importar retorno CNAB.'
          );
          this.selecionados.clear();
          this.carregar();
        },
        error: (err: any) => {
          this.util.closeLoading();
          this.util.toasterShowError('Erro', err?.message || 'Falha ao gerar remessa.');
        }
      });
  }

  formatMoney(value: number | null): string {
    if (value == null) {
      return '-';
    }
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  simNao(v: boolean): string {
    return v ? 'Sim' : 'Não';
  }

  paginaAnterior(): void {
    if (this.paginaAtual > 1) {
      this.paginaAtual--;
      this.atualizarPaginacao();
    }
  }

  proximaPagina(): void {
    if (this.paginaAtual < this.totalPaginas) {
      this.paginaAtual++;
      this.atualizarPaginacao();
    }
  }

  alterarItensPorPagina(): void {
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  private atualizarPaginacao(): void {
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    const fim = inicio + this.itensPorPagina;
    this.itensPaginados = this.itens.slice(inicio, fim);
  }

  private ordenarParDatas(a: string, b: string): [string, string] {
    const aa = (a || '').trim();
    const bb = (b || '').trim();
    if (!aa || !bb) {
      return [aa, bb];
    }
    return aa <= bb ? [aa, bb] : [bb, aa];
  }

  private desempacotarResponse(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.util.converterParaLowercase(atual ?? {});
  }

  private normalizarLista(data: any): any[] {
    if (!data) {
      return [];
    }
    if (Array.isArray(data)) {
      return data;
    }
    if (typeof data === 'object') {
      if (Object.prototype.hasOwnProperty.call(data, 'id') || Object.prototype.hasOwnProperty.call(data, 'descricao')) {
        return [data];
      }
      return Object.values(data).filter((x: any) => x && typeof x === 'object');
    }
    return [];
  }
}
